/**
 * NdaModal — the signed Confidentiality Agreement pop-up (matching the
 * prototype's `.ag-modal`). Auto-opens on first generation with the
 * "Agreement created" banner; re-opens on "View agreement" with the
 * "Your signed agreement" banner. British English.
 *
 * Behaviour ported from the prototype:
 * - Escape key closes; X button closes.
 * - Backdrop click does NOT close (deliberate — the agreement is "presented").
 * - Body scroll locked while open; modal scroll resets to top on open.
 * - Print button prints only the document (via a body class + print CSS).
 * - Download button fetches the server-side PDF + saves it.
 * - Re-send link triggers a re-send of the email via the BFF.
 */
import { useEffect, useRef } from 'react';
import { Button } from './Button';
import { NdaDocument } from './NdaDocument';
import type { DiscloseeDetails } from '../types/ndaText.js';
import './NdaModal.css';

export interface NdaModalProps {
  open: boolean;
  /** true = first generation ("Agreement created"); false = re-view ("Your signed agreement"). */
  isNew: boolean;
  /** The inventor's email (shown in the banner). */
  email: string | null;
  /** Agreement details for rendering the document. null = not yet populated. */
  disclosee: DiscloseeDetails | null;
  dateLabel: string;
  reference: string;
  signedAt: string;
  onClose: () => void;
  onDownload: () => void;
  onResend: () => void;
}

export function NdaModal({
  open,
  isNew,
  email,
  disclosee,
  dateLabel,
  reference,
  signedAt,
  onClose,
  onDownload,
  onResend,
}: NdaModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Lock body scroll + handle Escape while open (matches prototype behaviour).
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Reset scroll to top on open (matches prototype: m.scrollTop = 0).
    if (modalRef.current) modalRef.current.scrollTop = 0;
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handlePrint = () => {
    document.body.classList.add('itk-ag-printing');
    const done = () => {
      document.body.classList.remove('itk-ag-printing');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    // Small delay so the print class is applied before the print dialog opens.
    setTimeout(() => {
      window.print();
      // Fallback cleanup if afterprint doesn't fire (some browsers).
      setTimeout(done, 800);
    }, 60);
  };

  if (!open || !disclosee) return null;

  return (
    <div
      className="itk-ag-modal on"
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="itkAgBannerTitle"
      aria-hidden="false"
    >
      <div className="itk-ag-card">
        <button className="itk-ag-x" type="button" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="itk-ag-banner">
          <span className="tick" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <h3 id="itkAgBannerTitle">{isNew ? 'Agreement created' : 'Your signed agreement'}</h3>
          <p>
            It has been emailed to <b>{email || 'your email address'}</b>. If you can't find it, check your spam
            folder or{' '}
            <a
              href="#"
              className="itk-ag-link-inline"
              onClick={(e) => {
                e.preventDefault();
                onResend();
              }}
            >
              re-send it
            </a>
            . You can read, print and download the agreement below.
          </p>
        </div>

        <div className="itk-ag-actions">
          <Button variant="secondary" pill size="sm" onClick={handlePrint}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>{' '}
            Print
          </Button>
          <Button variant="primary" pill size="sm" onClick={onDownload}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>{' '}
            Download
          </Button>
        </div>

        <NdaDocument
          disclosee={disclosee}
          dateLabel={dateLabel}
          reference={reference}
          signedAt={signedAt}
        />
      </div>
    </div>
  );
}